import React from 'react'
import { formatDollars } from '../../../other/money'
import ArrowIcon from '../../../components/shared/icons/ArrowIcon'
import SparkleIcon from '../../../components/shared/icons/SparkleIcon'
import CheckMarkIcon from '../../../components/shared/icons/CheckMarkIcon'

// Safe wrapper around formatDollars that handles nullable values and various money object formats
const formatMoneyValue = value => {
  if (value === null || value === undefined) {
    return '$0.00'
  }

  // Handle string values (formatted currency like "$3.90")
  if (typeof value === 'string') {
    if (value.includes('$')) {
      return value
    }
    // If it's a string with no dollar sign, try to format it
    return `$${parseFloat(value).toFixed(2)}`
  }

  // Handle different money object formats
  if (typeof value === 'object') {
    // First priority: use formatted value if available
    if (value.formatted) return value.formatted

    // Second priority: use dollars if available
    if (value.dollars !== undefined) return formatDollars(value.dollars * 100)

    // Third priority: use cents if available
    if (value.cents !== undefined) return formatDollars(value.cents)
  }

  // Default to $0.00 for any other case
  return '$0.00'
}

// Helper function to display money values
const displayValue = value => <span>{formatMoneyValue(value)}</span>

// Check if any hardship data exists
const hasHardshipData = info =>
  info.hardship_statement ||
  (info.hardship && Object.values(info.hardship).some(value => value !== '0' && value))

// Check if there is any cash flow data
const hasCashFlowData = info => {
  // First try to check if we have a string value (either legacy or detailed data)
  if (
    typeof info.monthly_income_total === 'string' ||
    typeof info.monthly_expenses_total === 'string'
  ) {
    // Check if either income or expense is not $0.00
    return (
      (info.monthly_income_total && info.monthly_income_total !== '$0.00') ||
      (info.monthly_expenses_total && info.monthly_expenses_total !== '$0.00')
    )
  }

  // Then check if we might have a money object (legacy data format)
  if (
    typeof info.monthly_income_total === 'object' ||
    typeof info.monthly_expenses_total === 'object'
  ) {
    return info.monthly_income_total?.cents > 0 || info.monthly_expenses_total?.cents > 0
  }

  // If we don't have either format, there's no cash flow data
  return false
}

// Helper to render financial details section - only called when has_detailed_data is true
const renderFinancialDetails = financialDetails => {
  if (!financialDetails) {
    console.log('No financial details to render')
    return null
  }

  console.log('Rendering financial details:', financialDetails)

  const incomeFields = [
    { key: 'net_employment_income', label: 'Employment income' },
    { key: 'unemployment_income', label: 'Unemployment income' },
    { key: 'retirement_income', label: 'Retirement income' },
    { key: 'other_income', label: 'Other income' },
  ]

  const expenseFields = [
    // Column 1
    { key: 'mortgage_payments', label: 'Mortgage payments' },
    { key: 'rent', label: 'Rent payments' },
    { key: 'auto_payments', label: 'Auto payments' },
    // Column 2
    { key: 'student_loan_payment', label: 'Student loan payments' },
    { key: 'credit_card_payments', label: 'Credit card payments' },
    { key: 'personal_loan_payments', label: 'Personal loan payments' },
    { key: 'other_loan_payments', label: 'Other loan payments' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'utilities', label: 'Utilities' },
    { key: 'telephone', label: 'Telephone' },
    // Column 3
    { key: 'general_expenses', label: 'General expenses' },
    { key: 'food', label: 'Food' },
    { key: 'transportation', label: 'Transportation' },
    { key: 'medical_expenses', label: 'Medical expenses' },
    { key: 'child_care', label: 'Child care' },
    { key: 'charitable_giving', label: 'Charitable giving' },
    { key: 'other_expenses', label: 'Other expenses' },
  ]

  // Function to render a financial item
  const renderItem = item => {
    const value = financialDetails[item.key]
    const displayedValue = displayValue(value)

    // Skip items with zero values to reduce clutter
    if (displayedValue === '$0.00') {
      return null
    }

    return (
      <p key={item.key} className='mb-1'>
        {item.label}: {displayedValue}
      </p>
    )
  }

  // Check if we have any non-zero income items
  const hasIncomeItems = incomeFields.some(
    field =>
      financialDetails[field.key] && formatMoneyValue(financialDetails[field.key]) !== '$0.00'
  )

  // Check if we have any non-zero expense items
  const hasExpenseItems = expenseFields.some(
    field =>
      financialDetails[field.key] && formatMoneyValue(financialDetails[field.key]) !== '$0.00'
  )

  console.log('Has income items:', hasIncomeItems)
  console.log('Has expense items:', hasExpenseItems)

  if (!hasIncomeItems && !hasExpenseItems) {
    console.log('No non-zero items to display')
    return null
  }

  return (
    <div className='row mt-3'>
      <div className='col-md-4'>
        {/* Income items - only shown if there's at least one non-zero item */}
        {hasIncomeItems && <>{incomeFields.map(field => renderItem(field))}</>}

        {/* First column of expense items */}
        {hasExpenseItems && <>{expenseFields.slice(0, 3).map(field => renderItem(field))}</>}
      </div>

      <div className='col-md-4'>
        {/* Second column of expense items */}
        {hasExpenseItems && expenseFields.slice(3, 10).map(field => renderItem(field))}
      </div>

      <div className='col-md-4'>
        {/* Third column of expense items */}
        {hasExpenseItems && expenseFields.slice(10).map(field => renderItem(field))}
      </div>
    </div>
  )
}

const AccountDetails = ({ account: info }) => {
  console.log('Account Info:', info)
  console.log('Financial Details:', info.financial_details)
  console.log('Has Detailed Data:', info.has_detailed_data)
  const downloadAuthorizationLetter = async (event, documentId) => {
    event.preventDefault()
    const linkElement = event.target
    const statusElement = document.getElementById(`download-status-${documentId}`)
    const errorElement = document.getElementById(`download-error-${documentId}`)

    errorElement.querySelector('span').textContent = ''
    errorElement.classList.add('d-none')

    linkElement.classList.add('d-none')
    statusElement.classList.remove('d-none')

    try {
      const response = await fetch('/collectors/api/render_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ customer_document_id: documentId }),
      })

      if (!response.ok) {
        throw new Error('Failed to download file')
      }

      // Get the blob from the response
      const blob = await response.blob()

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob)

      // Create a sanitized customer name for the filename
      const sanitizedName = info.civil_number
        ? info.civil_number.replace(/[^a-z0-9]/gi, '_').toLowerCase()
        : 'authorization_letter'

      // Create and trigger download
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = `authorization_letter__${sanitizedName}.pdf`
      document.body.appendChild(downloadLink)
      downloadLink.click()

      // Cleanup
      document.body.removeChild(downloadLink)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error:', error)
      errorElement.querySelector('span').textContent = error.message
      errorElement.classList.remove('d-none')
    } finally {
      statusElement.classList.add('d-none')
      linkElement.classList.remove('d-none')
    }
  }

  const downloadSettlementAgreement = async (event, documentId) => {
    event.preventDefault()
    const linkElement = event.target
    const statusElement = document.getElementById(`download-settlement-status-${documentId}`)
    const errorElement = document.getElementById(`download-settlement-error-${documentId}`)

    errorElement.querySelector('span').textContent = ''
    errorElement.classList.add('d-none')

    linkElement.classList.add('d-none')
    statusElement.classList.remove('d-none')

    try {
      const response = await fetch('/collectors/api/settlement_agreement', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ customer_document_id: documentId }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to download settlement agreement')
      }

      // Get the blob from the response
      const blob = await response.blob()

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob)

      // Create a sanitized customer name for the filename
      const sanitizedName = info.civil_number
        ? info.civil_number.replace(/[^a-z0-9]/gi, '_').toLowerCase()
        : 'settlement_agreement'

      // Create and trigger download
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = `settlement_agreement__${sanitizedName}.pdf`
      document.body.appendChild(downloadLink)
      downloadLink.click()

      // Cleanup
      document.body.removeChild(downloadLink)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error:', error)
      errorElement.querySelector('span').textContent = error.message
      errorElement.classList.remove('d-none')
    } finally {
      statusElement.classList.add('d-none')
      linkElement.classList.remove('d-none')
    }
  }

  const downloadHistoryCSV = (documentId, referenceNumber, events) => {
    const sanitizeForCSV = str => {
      if (!str) return ''
      return `"${str
        .toString()
        .replace(/"/g, '""')
        .replace(/[\n\r]+/g, ' ')}"`
    }

    // Updated header row order
    const csvRows = [
      ['Account Information', '', '', '', ''].join(','),
      ['Plaintiff Name', sanitizeForCSV(info.plaintiff_name || 'n/a'), '', '', ''].join(','),
      ['Lawsuit State', sanitizeForCSV(info.lawsuit_state || 'n/a'), '', '', ''].join(','),
      ['Consumer Name', sanitizeForCSV(info.customer_name || 'n/a'), '', '', ''].join(','),
      ['Reference Number', sanitizeForCSV(info.civil_number || 'n/a'), '', '', ''].join(','),
      ['', '', '', '', ''].join(','),
      ['Activity Log', '', '', '', ''].join(','),
      ['Date', 'Time', 'Party', 'Reference', 'Action'].join(','), // Reordered columns
    ]

    events.forEach(event => {
      const date = new Date(event.created_at)
      let actionText = info.event_id_to_english_text[event.id]

      if (event.disclosures?.length > 0) {
        actionText += ' The collector made the following disclosures: '
        actionText += event.disclosures.map(disclosure => disclosure.text).join(' ')
      }

      // Reordered row data to match new column order
      const row = [
        sanitizeForCSV(
          date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
        ),
        sanitizeForCSV(date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })),
        sanitizeForCSV(
          event.party === 'customer'
            ? 'Consumer'
            : event.party.charAt(0).toUpperCase() + event.party.slice(1)
        ),
        sanitizeForCSV(referenceNumber),
        sanitizeForCSV(actionText),
      ]
      csvRows.push(row.join(','))
    })

    // Create and trigger download
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `activity_log_${documentId}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className='card-body m-3'>
      <h2 className='mb-2'>Account Information</h2>
      <p className='mb-1'>
        <strong>Plaintiff Name:</strong> {info.plaintiff_name || 'n/a'}
      </p>
      <p className='mb-1'>
        <strong>Lawsuit State:</strong> {info.lawsuit_state || 'n/a'}
      </p>
      <p className='mb-1'>
        <strong>Consumer Name:</strong> {info.customer_name || 'n/a'}
      </p>
      <p className='mb-1'>
        <strong>Reference Number:</strong> {info.civil_number || 'n/a'}
      </p>
      <p className='mb-1'>
        <strong>Social Security Number:</strong>{' '}
        {info?.social_security_number?.social_security_number ||
          (info?.social_security_number?.social_security_number_last_four
            ? `XXX-XX-${info.social_security_number.social_security_number_last_four}`
            : 'n/a')}
      </p>
      <p className='mb-1'>
        <strong>Date of Birth:</strong> {info.birthdate}
      </p>
      <p className='mb-1'>
        <strong>Address:</strong>{' '}
        {info.customer_mailing_address
          ? `${info.customer_mailing_address.customer_street}, ${info.customer_mailing_address.customer_city}, ${info.customer_mailing_address.customer_state} ${info.customer_mailing_address.customer_zip_code}`
          : 'n/a'}
      </p>
      <p className='mb-1'>
        <strong>SoloSettle Email:</strong> {info.email || 'n/a'}
      </p>
      <p className='mb-1'>
        <strong>SoloSettle ID:</strong> {info.customer_document?.id || 'n/a'}
      </p>
      <p className='mb-1'>
        {info.lawsuit_exists ? 'Litigation Account' : 'Pre-litigation Account'}
      </p>

      {info.settlement_negotiation_events?.length > 0 && (
        <>
          <h3 className='mt-4 mb-2'>Documents</h3>
          <div className='mb-3 ms-5'>
            <div className='mb-1'>
              <a
                href='#'
                className='text-primary'
                id={`download-link-${info.customer_document.id}`}
                onClick={e => downloadAuthorizationLetter(e, info.customer_document.id)}
              >
                Download authorization letter
              </a>
              <div id={`download-status-${info.customer_document.id}`} className='d-none'>
                <span className='text-dark'>
                  Downloading...{' '}
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  ></span>
                </span>
              </div>
              <div id={`download-error-${info.customer_document.id}`} className='d-none'>
                <span className='text-danger'></span>
              </div>
            </div>

            {info.latest_event?.action === 'signed_settlement_agreement' && (
              <div className='mb-1'>
                <a
                  href='#'
                  className='text-primary'
                  id={`download-settlement-link-${info.customer_document.id}`}
                  onClick={e => downloadSettlementAgreement(e, info.customer_document.id)}
                >
                  Download settlement agreement
                </a>
                <div
                  id={`download-settlement-status-${info.customer_document.id}`}
                  className='d-none'
                >
                  <span className='text-dark'>
                    Downloading...{' '}
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    ></span>
                  </span>
                </div>
                <div
                  id={`download-settlement-error-${info.customer_document.id}`}
                  className='d-none'
                >
                  <span className='text-danger'></span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Hardship information section */}
      {hasHardshipData(info) && (
        <>
          <h3 className='mt-4 mb-2'>Hardship information</h3>

          {info.hardship_statement && (
            <div className='mb-3 ms-5'>
              <p className='mb-2'>
                <span
                  className='fw-bold ai-review-container'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#495057',
                    fontSize: '1.1rem',
                  }}
                >
                  <span className='ai-review-icon'>
                    <SparkleIcon
                      width={28}
                      height={28}
                      style={{ marginRight: '8px', marginBottom: '0' }}
                    />
                  </span>
                  AI Summary
                </span>
              </p>
              <p className='mb-1' style={{ color: '#495057' }}>
                {info.hardship_statement}
              </p>
            </div>
          )}

          {info.hardship && (
            <div className='mb-3 ms-5'>
              {Object.entries(info.hardship)
                .filter(([key, value]) => !key.endsWith('_details') && value !== '0' && value)
                .map(([key, value]) => (
                  <div key={key}>
                    <p className='mb-1 fw-semibold'>{value}</p>
                    {info.hardship[`${key}_details`] && (
                      <p className='mb-2' style={{ fontSize: '0.9em', color: '#6c757d' }}>
                        {info.hardship[`${key}_details`]}
                      </p>
                    )}
                  </div>
                ))}
            </div>
          )}
        </>
      )}

      {/* Cash flow statement section */}
      {hasCashFlowData(info) && (
        <>
          <h3 className='mt-4 mb-2'>
            Cash flow statement
            {info.plaid_verified && (
              <span
                className='ms-3 d-inline-flex align-items-center'
                title='Verified with data from Plaid'
              >
                <CheckMarkIcon />
                <span className='ms-2 fs-6 fw-normal text-dark' style={{ opacity: 0.85 }}>
                  Verified by Plaid
                </span>
              </span>
            )}
          </h3>
          <div className='ms-5'>
            <p className='mb-2 fw-bold'>Summary</p>

            {/* Income and expense values will be either formatted strings or money objects */}
            <p className='mb-1'>Monthly income: {displayValue(info.monthly_income_total)}</p>
            <p className='mb-1'>Monthly expenses: {displayValue(info.monthly_expenses_total)}</p>

            {/* Show detailed breakdown only when user has completed the detailed form and has financial details */}
            {info.has_detailed_data &&
              info.financial_details &&
              renderFinancialDetails(info.financial_details)}
          </div>
        </>
      )}

      <br />

      <h3 className='mt-4 mb-2'>Payment tiers</h3>
      <div className='ms-5'>
        {info.latest_historical_offer?.phases &&
          info.latest_historical_offer?.phases.length > 1 && (
            <table className='table table-sm table-borderless'>
              <thead>
                <tr>
                  <th>Tier</th>
                  <th># Pays</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {info.latest_historical_offer.phases.map((phase, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{phase.iterations}</td>
                    <td>{formatDollars(phase.payment_amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {!info.latest_historical_offer?.phases ||
          (info.latest_historical_offer?.phases.length === 0 && (
            <table className='table table-sm table-borderless'>
              <thead>
                <tr>
                  <th>Tier</th>
                  <th># Pays</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>{info.latest_historical_offer.payments}</td>
                  <td>{formatDollars(info.latest_historical_offer.total_amount_cents)}</td>
                </tr>
              </tbody>
            </table>
          ))}
      </div>

      <br />

      <h3 className='mt-4 mb-2'>History</h3>
      <div className='ms-5'>
        <div className='d-flex justify-content-end mb-2'>
          <a
            href='#'
            className='text-decoration-none'
            onClick={e => {
              e.preventDefault()
              downloadHistoryCSV(
                info.customer_document.id,
                info.civil_number,
                info.settlement_negotiation_events || []
              )
            }}
          >
            <ArrowIcon />
          </a>
        </div>
        <table className='table table-sm table-borderless'>
          <thead>
            <tr>
              <th>DATE</th>
              <th>TIME</th>
              <th>PARTY</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {(info.settlement_negotiation_events || []).map((event, index) => {
              const disclosures = event.disclosures?.map(d => d.text) || []
              return (
                <tr key={index}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {new Date(event.created_at).toLocaleDateString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {new Date(event.created_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>
                    {event.party === 'customer'
                      ? 'Consumer'
                      : event.party.charAt(0).toUpperCase() + event.party.slice(1)}
                  </td>
                  <td>
                    {info.event_id_to_english_text[event.id]}

                    {disclosures.length > 0 && (
                      <div>The collector made the following disclosures:</div>
                    )}
                    {disclosures.map(disclosureText => (
                      <div key={disclosureText}>{disclosureText}</div>
                    ))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AccountDetails
